import React, { StrictMode, Fragment, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { initLocale } from './i18n'
initLocale();

// Preload above-the-fold LCP category tile images so the browser discovers
// them immediately, before React renders <CategoryTilesGrid />.
import lcpApparel from '@/assets/preview/category-apparel.webp';
import lcpDresses from '@/assets/preview/category-dresses.webp';
import lcpGoddess from '@/assets/preview/category-goddess.webp';
if (typeof document !== 'undefined' && window.location.pathname === '/') {
  [lcpApparel, lcpDresses, lcpGoddess].forEach((href, i) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    if (i === 2) link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
  });
}

// REMOVED CartProvider - Shopify-only cart system now
// CRITICAL: Lazy load PerformanceHUD to prevent web-vitals from loading during SSR
const PerformanceHUD = lazy(() => import('./components/PerformanceHUD').then(m => ({ default: m.PerformanceHUD || (() => null) })));
import { IS_DEVELOPMENT, FEATURE_FLAGS } from './utils/production-config';
import { productionLogger } from './utils/production-logger';
// Temporarily disabled problematic imports
// import { consoleReplacer } from './utils/consoleReplacer';
// import { applySecurityHeaders } from './utils/securityHeadersConfig';
// import { emergencyCacheClear } from './utils/clearCacheHelper';

// Disabled to prevent deployment issues
// Make cache clearing utility globally accessible for debugging
// if (typeof window !== 'undefined') {
//   (window as any).clearAllCaches = emergencyCacheClear;
// }

// Filter out known harmless warnings early in dev mode
if (!import.meta.env.PROD) {
  const originalWarn = console.warn;
  console.warn = (message?: any, ...args: any[]) => {
    if (typeof message === 'string' && 
        (message.includes('iframe which has both allow-scripts and allow-same-origin') ||
         message.includes('Deprecated API for given entry type'))) {
      return; // Suppress HCaptcha and browser API warnings
    }
    originalWarn(message, ...args);
  };
}

// Disabled security headers initialization to prevent deployment issues
// Initialize security headers only (console replacement disabled to prevent React errors)
// requestIdleCallback ? requestIdleCallback(() => {
//   applySecurityHeaders();
// }) : setTimeout(() => {
//   applySecurityHeaders();
// }, 0);

// Initialize utilities with error handling

// CRITICAL: Remove any lingering fatal-error elements from old cached code
if (typeof document !== 'undefined') {
  const fatalError = document.getElementById('fatal-error');
  if (fatalError) {
    fatalError.remove();
  }
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  try {
    const rootElement = document.getElementById("root");
    if (!rootElement) {
      throw new Error('Root element not found');
    }
    
    createRoot(rootElement).render(
      <StrictMode>
        <App />
        {FEATURE_FLAGS.showPerformanceHUD && (
          <Suspense fallback={null}>
            <PerformanceHUD />
          </Suspense>
        )}
      </StrictMode>,
    );
  } catch (e: any) {
    productionLogger.error('App failed to render:', e);
    
    // Emergency fallback: show basic content if React fails completely
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `
        <div style="padding: 2rem; font-family: system-ui; background: #FCF8F4; color: #443E42; min-height: 100dvh;">
          <h1>Boho Style Co.</h1>
          <p>We're experiencing technical difficulties. Please try refreshing the page.</p>
          <button onclick="window.location.reload()" style="padding: 0.5rem 1rem; background: #8B7355; color: white; border: none; border-radius: 4px; cursor: pointer;">Refresh Page</button>
        </div>
      `;
    }
  }
}

// Service worker disabled to fix deployment issues
// requestIdleCallback ? requestIdleCallback(() => {
//   handleServiceWorker();
// }) : setTimeout(handleServiceWorker, 0);

function handleServiceWorker() {
  try {
    // Only proceed if service workers are supported and enabled
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && FEATURE_FLAGS.enableServiceWorker) {
      import('./utils/serviceWorker').then(({ register }) => {
        register();
      }).catch((error) => {
        console.warn('Service worker registration failed:', error);
        // Don't throw - service worker is progressive enhancement
      });
    } else if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      // If service workers exist but are disabled, clean up any existing registrations
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      }).catch(() => {
        // Ignore cleanup errors
      });
      if (window.caches) {
        caches.keys().then(keys => {
          keys.forEach(k => caches.delete(k));
        }).catch(() => {
          // Ignore cleanup errors
        });
      }
    }
  } catch (err) {
    console.warn('Service worker management failed:', err);
    // Don't throw - this shouldn't break the app
  }
}
